/**
 * « Maintenant » : la seule chose qui compte à cet instant, en tête d'un écran
 * d'accueil — l'intervention en cours ou la suivante sur le terrain, ce qui
 * réclame le bureau tout de suite, ou un état calme quand rien ne presse.
 *
 * Une action principale (le geste le plus utile qui existe déjà : ouvrir,
 * prendre en charge…), au plus une action secondaire (un lien de l'application
 * ou un lien externe comme l'itinéraire), un bouton déjà composé (« Appeler »)
 * et un menu « … » pour le reste, en haut à droite de la carte. Le retour
 * d'une action (`children`, par exemple Banner) s'affiche dans la
 * carte, au-dessus des boutons.
 */
import type { ReactNode } from 'react';
import { ActionMenu, type ActionMenuItem } from '../action-menu.js';
import { ActionButton, type Action } from './action.js';
import { useHeadingLevel } from './PageHeader.js';

/** Lien hors de l'application (itinéraire), ouvert dans un nouvel onglet. */
export interface ExternalLink {
  label: string;
  href: string;
  icon?: ReactNode;
}

function isExternal(action: Action | ExternalLink): action is ExternalLink {
  return 'href' in action;
}

type Heading = 'h2' | 'h3' | 'h4';

export function NowCard(props: {
  /** Ce que la carte désigne : « Maintenant », « Ensuite ». C'est le titre de la région. */
  heading: string;
  title: ReactNode;
  /** Contexte, du plus utile au moins utile (client et site, adresse, heure). */
  lines?: ReactNode[];
  badge?: ReactNode;
  icon?: ReactNode;
  /** `urgent` : ce qui ne peut pas attendre ; `calm` : rien ne presse. */
  tone?: 'default' | 'urgent' | 'calm';
  primary?: Action;
  /**
   * Une urgence est montrée juste au-dessus : son geste est l'action principale de l'écran,
   * celui de cette carte passe en secondaire.
   */
  demoted?: boolean;
  secondary?: Action | ExternalLink;
  /** Un bouton déjà composé, propre au métier (« Appeler » et son choix d'appel masqué). */
  extra?: ReactNode;
  menu?: ActionMenuItem[];
  /** Retour de la dernière action (en attente, en file, confirmée, échouée). */
  children?: ReactNode;
}): ReactNode {
  const level = useHeadingLevel();
  const HeadingTag = `h${Math.min(level, 4)}` as Heading;
  const tone = props.tone ?? 'default';
  const classes = ['ui-now', tone !== 'default' && `ui-now--${tone}`].filter(Boolean).join(' ');
  const secondary = props.secondary;
  const hasActions = Boolean(props.primary || secondary || props.extra);
  return (
    <section className={classes} data-ui="now-card" aria-label={props.heading}>
      <div className="ui-now__head">
        <HeadingTag className="ui-now__heading">
          {props.icon ? <span className="ui-now__icon" aria-hidden="true">{props.icon}</span> : null}
          {props.heading}
        </HeadingTag>
        {props.badge || props.menu?.length ? (
          <span className="ui-now__aside">
            {props.badge}
            {props.menu?.length ? <ActionMenu items={props.menu} /> : null}
          </span>
        ) : null}
      </div>
      <strong className="ui-now__title">{props.title}</strong>
      {(props.lines ?? []).filter(Boolean).map((line, index) => (
        <span key={index} className="ui-now__line">{line}</span>
      ))}
      {props.children}
      {hasActions ? (
        <div className="ui-now__actions">
          {props.primary ? <ActionButton action={props.primary} large variant={props.demoted ? 'secondary' : 'primary'} className="ui-now__primary" /> : null}
          {secondary ? (
            isExternal(secondary) ? (
              <a className="btn btn--secondary btn--large" href={secondary.href} target="_blank" rel="noreferrer">
                {secondary.icon}
                <span>{secondary.label}</span>
              </a>
            ) : (
              <ActionButton action={secondary} variant="secondary" large />
            )
          ) : null}
          {props.extra}
        </div>
      ) : null}
    </section>
  );
}
